"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { copyToClipboard, type CopyStatus } from "@/lib/clipboard";

/**
 * "KOPIÉR" -> "KOPIERET!" -> "KOPIÉR" — THE STATE MACHINE, ONCE
 *
 * The I/O half has always been shared (`lib/clipboard.ts#copyToClipboard`,
 * which narrows a rejected `writeText` to a boolean). What was written out
 * four times is what happens AFTER it: a `useState`, a set to "copied", and a
 * `setTimeout` back to idle — the same two seconds in all four.
 *
 * AND ALL FOUR CARRIED THE SAME BUG, which is the reason this exists rather
 * than the duplication: none of them cleared the timer on unmount. A menu
 * closed, or a page navigated away from, inside the two-second window left a
 * timer holding a setter for a component that no longer exists. The cleanup
 * below is the fix, and it belongs in the hook so it cannot be forgotten a
 * fifth time.
 *
 * `failed` IS A STATE, NOT AN ABSENCE. `clipboard.ts` says why: a denied
 * permission, a blocking extension or a document that lost focus all reject,
 * and a button that quietly returns to "Kopiér" claims nothing happened when
 * something did. `copy` also returns the boolean, so a caller that wants to
 * say more than the label can (an inline error, a menu it keeps open) still
 * can.
 *
 * TWO SHAPES, ONE HOOK. A lone button reads `status`; a list of rows passes a
 * key and reads `statusOf(key)`, so only the row that was clicked relabels.
 * Serving both is not generosity — the fourth call site is a list, and a hook
 * that only did the single case would have left it hand-rolled.
 */

/** How long "Kopieret!" stands before the button says "Kopiér" again. */
const RESET_MS = 2000;

type CopyKey = string | number | null;

export function useCopyToClipboard(resetMs: number = RESET_MS) {
  const [state, setState] = useState<{ key: CopyKey; status: CopyStatus }>({
    key: null,
    status: "idle",
  });
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  const copy = useCallback(
    async (text: string, key: CopyKey = null): Promise<boolean> => {
      const ok = await copyToClipboard(text);
      // The write is asynchronous, so the component can be gone by the time it
      // settles — the same window the timer had, one await earlier.
      if (!alive.current) return ok;

      if (timer.current) clearTimeout(timer.current);
      setState({ key, status: ok ? "copied" : "failed" });
      timer.current = setTimeout(() => {
        if (alive.current) setState({ key: null, status: "idle" });
      }, resetMs);
      return ok;
    },
    [resetMs],
  );

  /** The status of one row. Every other row is idle, whatever this one is. */
  const statusOf = useCallback(
    (key: CopyKey): CopyStatus => (state.key === key ? state.status : "idle"),
    [state],
  );

  return {
    /** For a single button — the keyless case. */
    status: statusOf(null),
    statusOf,
    copy,
  };
}
